// MCP connector SDK bridge — client-side wrappers over the server's
// /core/mcp-connectors routes (Mongo-backed). Secrets (auth tokens) are
// written server-side via integration_secrets and referenced by
// auth_secret_id FK — they never travel through the browser in plain text.
//
// Runtime contract: JSON-RPC 2.0 over SSE / WebSocket / HTTP transport.
// Capability discovery (tools/list + resources/list) runs server-side on
// install and is cached in the `capabilities` column.
//
// Scoping: the server filters rows by the authenticated active workspace,
// so a user only ever sees their own workspace's connectors. The reader
// wire shape is snake_case ({ id, name, url, scope_type, scope_id, status,
// transport, auth_kind, capabilities }), so the admin UI + canvas rail
// don't branch.
//
// (The legacy governance-Supabase read/write path — `_gov().from('mcp_connectors')`
// gated on __USE_CORE_MCP__ — was removed with that project; /core/mcp-connectors
// is now the sole source.)

import { getSDK } from '../sdk.js'

// ── Helpers ───────────────────────────────────────────────────────────────────
//
// SDK-only transport (integrations remediation #6 — tickets/server.md). This
// module used to own a hand-rolled `_serverFetch`: its own channel-base
// resolution, its own localStorage token read, and a bare `{ error }` return
// convention — one of the three raw-fetch leaves that blocked normalising the
// integrations response envelope. `_viaSdk` keeps that RETURN CONTRACT
// (payload on success, `{ error }` on failure) so none of the exported
// functions below had to change shape, while auth + base URL move to the
// SDK's TokenManager / channel resolver.
const _viaSdk = async (run) => {
  try {
    const res = await run(getSDK())
    return res || { error: 'empty response' }
  } catch (err) {
    // The SDK throws on non-2xx and on transport failure; surface both in
    // the legacy shape, preserving the HTTP status when the error carries it.
    const status = err?.status || err?.statusCode
    return { error: err?.message || 'network error', ...(status ? { status } : {}) }
  }
}

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * List all MCP connectors visible to the active workspace, optionally filtered
 * by scope.
 *
 * @param {{ scopeType?: 'org'|'workspace'|'project', scopeId?: string }} opts
 * @returns {Promise<{ data: object[], error: string|null }>}
 */
export const listConnectors = async ({ scopeType, scopeId } = {}) => {
  const qs = new URLSearchParams()
  if (scopeType) qs.set('scope_type', scopeType)
  if (scopeId != null) qs.set('scope_id', String(scopeId))
  const res = await _viaSdk((sdk) => sdk.mcpConnectorList(Object.fromEntries(qs)))
  if (res?.error) return { data: [], error: res.error }
  return { data: res?.connectors || [], error: null }
}

// ── Write ─────────────────────────────────────────────────────────────────────

/**
 * Install a new MCP connector. Capability discovery (tools/list +
 * resources/list) is triggered server-side via POST /core/mcp-connectors;
 * this client function only creates the row and kicks the discovery task.
 *
 * @param {{ workspaceId: string, scopeType: string, scopeId?: string,
 *            name: string, url: string, transport?: string, authKind?: string,
 *            authToken?: string }} opts
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export const installConnector = async ({
  workspaceId,
  scopeType = 'workspace',
  scopeId = null,
  name,
  url,
  transport = 'sse',
  authKind = 'none',
  authToken
}) => {
  if (!workspaceId) return { ok: false, error: 'workspaceId required' }
  if (!url) return { ok: false, error: 'url required' }
  if (!name) return { ok: false, error: 'name required' }

  // Route through the server endpoint so it can store the secret in Vault
  // and run capability discovery without exposing the token to the DB.
  const result = await _viaSdk((sdk) =>
    sdk.mcpConnectorCreate({
      workspaceId,
      scopeType,
      scopeId,
      name,
      url,
      transport,
      authKind,
      authToken
    })
  )

  if (result.error) return { ok: false, error: result.error }
  return { ok: true, data: result }
}

/**
 * Re-run tools/list + resources/list for a connector and refresh the
 * `capabilities` cache.
 *
 * @param {string} connectorId — id of the mcp_connectors row.
 * @returns {Promise<{ ok: boolean, capabilities?: object, error?: string }>}
 */
export const discoverCapabilities = async (connectorId) => {
  if (!connectorId) return { ok: false, error: 'connectorId required' }
  const result = await _viaSdk((sdk) => sdk.mcpConnectorDiscover(connectorId))
  if (result.error) return { ok: false, error: result.error }
  return { ok: true, capabilities: result.capabilities || result }
}

/**
 * Enable a connector (sets status = 'enabled').
 *
 * @param {string} connectorId
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const enableConnector = async (connectorId) => {
  const res = await _viaSdk((sdk) => sdk.mcpConnectorSetStatus(connectorId, { status: 'enabled' }))
  return res?.error ? { ok: false, error: res.error } : { ok: true }
}

/**
 * Disable a connector (sets status = 'disabled').
 *
 * @param {string} connectorId
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const disableConnector = async (connectorId) => {
  const res = await _viaSdk((sdk) => sdk.mcpConnectorSetStatus(connectorId, { status: 'disabled' }))
  return res?.error ? { ok: false, error: res.error } : { ok: true }
}

/**
 * Delete a connector row permanently.
 *
 * @param {string} connectorId
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const deleteConnector = async (connectorId) => {
  const res = await _viaSdk((sdk) => sdk.mcpConnectorRemove(connectorId))
  return res?.error ? { ok: false, error: res.error } : { ok: true }
}
